/**
 * ترجیح‌های کاربر که فقط روی همین دستگاه معنی دارن.
 *
 * الگو عمداً همون الگوی theme.ts است: یه آبجکتِ **زنده** که سرِ جاش پر
 * می‌شه (نه جایگزین) به‌علاوه‌ی شنونده‌ها. چون فایل‌ها ممکنه مقدار رو موقعِ
 * بارگذاری بگیرن، جایگزین کردنِ خودِ آبجکت مرجع‌های قدیمی رو کهنه می‌کنه.
 *
 * چرا روی سرور نیست: این‌ها ترجیحِ نمایش‌اند، نه داده‌ی حساب. سینک کردنشون
 * یه جدول و یه مسیرِ خطای تازه می‌خواد در ازای چیزی که کاربر یه‌بار تنظیم
 * می‌کنه و تمام.
 */
import { storage } from '../platform.ts';
import { recordPref } from './prefSink.ts';

const KEY = 'whosup.prefs.v1';

/**
 * شکلِ ردیف‌های صفحه‌ی کشف.
 *
 * - `compact`: فقط عنوان و تاریخ، بدونِ هیچ دکمه‌ای - برای وقتی که دنبالِ
 *   یه چیزِ مشخصی می‌گردی و می‌خوای بیشترین تعداد ردیف توی یه صفحه جا شه.
 * - `magazine`: عنوان + عکسِ کوچیک + خلاصه. حالتِ پیش‌فرض و همونی که
 *   قبلاً تنها حالت بود.
 * - `photo`: عکسِ تمام‌عرض و مشخصات زیرش - برای مرور کردن، نه گشتن.
 * - `map`: همون فهرست، ولی روی نقشه. قبلاً سربرگِ جدا بود؛ ولی «کجاست؟»
 *   هم یه جور **دیدنِ** همون فهرسته، نه یه فهرستِ دیگه — و تا وقتی سربرگ
 *   بود، عوض کردنِ فیلتر یعنی بیرون اومدن از نقشه.
 *
 * چرا این‌جا (core) و نه توی features/discover: مقدارش این‌جا ذخیره می‌شه و
 * core نباید به features وابسته شه.
 */
export type DiscoverView = 'compact' | 'magazine' | 'photo' | 'map';

const DISCOVER_VIEWS: DiscoverView[] = ['compact', 'magazine', 'photo', 'map'];

/**
 * ترتیبِ فهرست‌های صفحه‌ی کشف.
 *
 * - `nextTime`: نزدیک‌ترین اجرای بعدی اول. پیش‌فرض، چون همون ترتیبیه که
 *   فهرست همیشه داشته و «چی این هفته هست؟» رایج‌ترین سوالِ این صفحه‌ست.
 * - `added`: تازه‌ترین چیزی که به اپ اضافه شده اول.
 * - `distance`: نزدیک‌ترین به من اول (اجازه‌ی موقعیت می‌خواد).
 * - `popularity`: عددی که سرور حساب می‌کنه و کاربر نمی‌بیندش - نگاه کن به
 *   مایگریشن 0049.
 * - `price`: رایگان، بعد تخفیف‌دار، بعد نامشخص، بعد قیمت‌دار.
 * - `title` / `titleDesc`: **فقط صاحبِ پروژه**. الفبایی بر اساسِ عنوانِ رخداد
 *   (یا نامِ مکان)، یه‌بار از الف تا ی و یه‌بار برعکس. ابزارِ خودِ اوست برای
 *   پیدا کردنِ یه رکوردِ مشخص توی فهرستِ کامل، نه ترتیبی که به کسی چیزی
 *   درباره‌ی رخدادها بگه — پس مثلِ `traceLog` به `TRACE_OWNER` بسته‌ست، نه
 *   به `isAdmin` (نگاه کن به sortsFor).
 * - `reviewPriority`: **فقط ادمین**. عددی که کرالر توی metadata می‌ذاره و
 *   یعنی «چقدر ارزش داره آدم این رو ببینه» - نگاه کن به مایگریشن 0052.
 *   توی فهرستِ گزینه‌ها هم فقط برای ادمین ظاهر می‌شه (sortsFor)، ولی نامش
 *   این‌جاست چون مثل بقیه روی همین گوشی ذخیره می‌شه.
 * - `flagged`: **فقط ادمین**. هرچی خودِ ادمین نشان کرده اول، تازه‌ترین نشان
 *   بالاتر - همون ستونِ `flagged_at` که سربرگِ «نشان‌دار»ِ صفِ بازبینی هم از
 *   روش ساخته می‌شه. فرقش با `reviewPriority` این‌که اون حدسِ کرالره و این
 *   تصمیمِ خودِ آدم: «این رو گذاشتم کنار که برگردم بهش».
 *
 * مثلِ DiscoverView این‌جاست نه توی features/discover: مقدارش این‌جا ذخیره
 * می‌شه و core نباید به features وابسته شه.
 */
export type DiscoverSort = 'nextTime' | 'added' | 'distance' | 'popularity' | 'price'
  | 'title' | 'titleDesc' | 'reviewPriority' | 'flagged';

const DISCOVER_SORTS: DiscoverSort[] = [
  'nextTime', 'added', 'distance', 'popularity', 'price',
  'title', 'titleDesc', 'reviewPriority', 'flagged',
];

/**
 * چیزی که این‌جا نیست هم حرف می‌زنه: یه مدت دو کلیدِ دیگه هم بود — «سیگنال‌های
 * من جدا» و «کنترل‌های کشف بالای صفحه». هر دو یه شکلِ دومِ همون صفحه بودن، و
 * شکلِ دوم یعنی دو تا مسیر برای نگه داشتن و یه سوالِ اضافه توی تنظیمات، در
 * ازای چیزی که شکلِ اول از قبل می‌تونست بگه. تنظیمات جای انتخاب‌هاییه که
 * جوابِ درستشون به آدم‌ها فرق می‌کنه، نه جای تصمیم‌های نگرفته.
 */
export interface Prefs {
  /** نمای فهرست‌های صفحه‌ی کشف - نگاه کن به DiscoverView. */
  discoverView: DiscoverView;
  /** ترتیبِ فهرست‌های صفحه‌ی کشف - نگاه کن به DiscoverSort. */
  discoverSort: DiscoverSort;
  /**
   * لاگِ روی دستگاه — کادرِ لاگ روی صفحه به‌علاوه‌ی فرستادنش به
   * `client_errors`. نگاه کن به core/lib/trace.
   *
   * برخلافِ دو تای بالا این یکی ترجیحِ نمایش نیست، ابزارِ تشخیصه: پیش‌فرضش
   * برای همه خاموشه و فقط از تنظیماتِ یه حسابِ مشخص روشن می‌شه. این‌جا
   * می‌شینه (نه توی کد) چون باید روی بیلدِ منتشرشده و بدونِ کامپایلِ دوباره
   * بشه روشنش کرد — وگرنه هر بار دیدنِ یه باگ یعنی یه انتشارِ کامل.
   *
   * اسمِ قبلیش `swipeTrace` بود، وقتی فقط ژستِ سواپ رو می‌نوشت.
   */
  traceLog: boolean;
}

const DEFAULTS: Prefs = {
  discoverView: 'magazine',
  discoverSort: 'nextTime',
  traceLog: false,
};

export const prefs: Prefs = { ...DEFAULTS };

let version = 0;
const listeners = new Set<() => void>();

/** برای StyleSheet و useMemoهایی که باید با عوض شدنِ ترجیح دوباره ساخته شن. */
export const prefsVersion = (): number => version;

export function onPrefsChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

const bump = () => { version += 1; listeners.forEach((fn) => fn()); };

export async function initPrefs(): Promise<void> {
  try {
    const raw = await storage().getItem(KEY);
    if (!raw) return;
    const saved = JSON.parse(raw) as Partial<Prefs>;
    // فقط کلیدهای شناخته‌شده؛ نسخه‌ی قدیمی نباید کلیدِ ناشناس تزریق کنه — و
    // کلیدِ برداشته‌شده‌ای که هنوز روی گوشیه، همین‌جا بی‌سروصدا نادیده می‌مونه.
    // نامِ نمای ذخیره‌شده هم بررسی می‌شه: نسخه‌ی بعدی ممکنه یه نما رو حذف کنه
    // و اون‌وقت مقدارِ روی گوشی به هیچ رندری نمی‌خوره.
    if (saved.discoverView && DISCOVER_VIEWS.includes(saved.discoverView)) {
      prefs.discoverView = saved.discoverView;
    }
    if (saved.discoverSort && DISCOVER_SORTS.includes(saved.discoverSort)) {
      prefs.discoverSort = saved.discoverSort;
    }
    // اسمِ قدیمی هم پذیرفته می‌شه: کلید روی گوشیِ کسی که همین حالا لاگ رو
    // روشن داره نشسته، و خاموش شدنش با یه بروزرسانی یعنی دقیقاً وقتی که
    // داریم دنبالِ یه باگ می‌گردیم، ابزارش از دست می‌ره.
    const legacy = (saved as { swipeTrace?: unknown }).swipeTrace;
    if (typeof saved.traceLog === 'boolean') prefs.traceLog = saved.traceLog;
    else if (typeof legacy === 'boolean') prefs.traceLog = legacy;
    bump();
  } catch {
    // ترجیحِ خوانده‌نشده یعنی پیش‌فرض — دلیلی برای شکستنِ بالا آمدنِ اپ نیست.
  }
}

export async function setPref<K extends keyof Prefs>(key: K, value: Prefs[K]): Promise<void> {
  if (prefs[key] === value) return;
  prefs[key] = value;
  bump();
  // فقط ثبت. مقدار همچنان از همین‌جا خوانده می‌شود و سرور رویش حرفی
  // نمی‌زند — نگاه کن به lib/prefSink. (نامِ کلید همان نامِ فیلد است، پس
  // فهرستِ PrefKey و این آبجکت با هم می‌مانند.)
  recordPref(key, value);
  try {
    await storage().setItem(KEY, JSON.stringify(prefs));
  } catch {
    // ذخیره نشد؛ توی همین نشست درست کار می‌کنه و دفعه‌ی بعد پیش‌فرضه.
  }
}
